import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrashCan,
  faGripDotsVertical,
} from "@fortawesome/pro-solid-svg-icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BatchActionPill } from "@components/CatalogIndex";
import { conditionSummary, itemTypeLabel, type Recommendation } from "./types";
import "./style.css";

export interface RecommendationsListProps {
  rows: Recommendation[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onAdd: () => void;
  onEdit: (rec: Recommendation) => void;
  onBatchDelete: () => void;
  onReorder: (activeId: string, overId: string) => void;
  itemName: (rec: Recommendation) => string;
}

const Row = ({
  rec,
  selectedIds,
  setSelectedIds,
  onEdit,
  itemName,
}: {
  rec: Recommendation;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onEdit: (rec: Recommendation) => void;
  itemName: (rec: Recommendation) => string;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `rec:${rec.id}` });
  const checked = selectedIds.includes(rec.id);
  const name = itemName(rec) || `Item #${rec.itemId}`;
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <td className="rec-grip" {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </td>
      <td>
        <input
          type="checkbox"
          style={{ accentColor: "#418172" }}
          aria-label={`Select recommendation ${name}`}
          checked={checked}
          onChange={() =>
            setSelectedIds(
              checked
                ? selectedIds.filter((x) => x !== rec.id)
                : [...selectedIds, rec.id]
            )
          }
        />
      </td>
      <td>
        <button
          type="button"
          className="adminListEditLink rec-edit-link"
          onClick={() => onEdit(rec)}
        >
          {name}
          <FontAwesomeIcon
            icon={faPencil}
            className="adminListEditLink__icon"
          />
        </button>
      </td>
      <td>{itemTypeLabel(rec.itemType)}</td>
      <td>{rec.quantityValue}</td>
      <td>{conditionSummary(rec)}</td>
    </tr>
  );
};

export const RecommendationsList = ({
  rows,
  selectedIds,
  setSelectedIds,
  onAdd,
  onEdit,
  onBatchDelete,
  onReorder,
  itemName,
}: RecommendationsListProps): JSX.Element => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(
      String(active.id).replace("rec:", ""),
      String(over.id).replace("rec:", "")
    );
  };

  return (
    <div className="recommendations-tab-card">
      <div className="recommendations-tab-card__header">
        <h2 className="recommendations-tab-card__title">
          Survey Recommendations
        </h2>
        <div className="recommendations-tab-card__actions">
          <BatchActionPill
            selectedCount={selectedIds.length}
            actions={[
              {
                icon: faTrashCan,
                label: "Delete",
                onClick: onBatchDelete,
                variant: "danger",
              },
            ]}
          />
          <Button className="squareGreenButton" onClick={onAdd}>
            Add Recommendation
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-muted p-2">No recommendations yet.</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="recommendations-table">
            <thead>
              <tr>
                <th />
                <th />
                <th>Item</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Applies When</th>
              </tr>
            </thead>
            <SortableContext
              items={rows.map((r) => `rec:${r.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {rows.map((rec) => (
                  <Row
                    key={rec.id}
                    rec={rec}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onEdit={onEdit}
                    itemName={itemName}
                  />
                ))}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      )}
    </div>
  );
};

export default RecommendationsList;
